import { useMode } from '@/providers/ModeProvider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Database, TestTube2, Wifi, WifiOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';

export function ModeToggle() {
  const { mode, toggleMode, isDemo, isLive } = useMode();
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleToggle = () => {
    setIsTransitioning(true);
    toggleMode();
    
    // Reset transition state after animation
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  return (
    <div className="border-t bg-card p-4">
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Label htmlFor="mode-toggle" className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Data Mode
          </Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5">
                {isDemo ? (
                  <TestTube2 className="h-3.5 w-3.5 text-amber-500" />
                ) : (
                  <Database className="h-3.5 w-3.5 text-green-500" />
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p className="font-semibold mb-1">
                {isDemo ? 'Demo Mode' : 'Live Mode'}
              </p>
              <p className="text-xs text-muted-foreground">
                {isDemo
                  ? 'Using mock data for demonstration. No backend connection required.'
                  : 'Connected to backend with real-time data. All changes are saved.'}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Toggle Switch */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all duration-200',
                isDemo ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400' : 'text-muted-foreground'
              )}
            >
              <TestTube2 className="h-4 w-4" />
              <span className="text-sm font-medium">Demo</span>
            </div>
          </div>

          <Switch
            id="mode-toggle"
            checked={isLive}
            onCheckedChange={handleToggle}
            className={cn(
              'transition-all duration-200',
              isTransitioning && 'scale-95'
            )}
          />

          <div className="flex items-center gap-2">
            <div
              className={cn(
                'flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-all duration-200',
                isLive ? 'bg-green-500/10 text-green-700 dark:text-green-400' : 'text-muted-foreground'
              )}
            >
              <Database className="h-4 w-4" />
              <span className="text-sm font-medium">Live</span>
            </div>
          </div>
        </div>

        {/* Status Indicator */}
        <div
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all duration-200',
            isDemo
              ? 'bg-amber-500/5 border border-amber-500/20 text-amber-700 dark:text-amber-400'
              : 'bg-green-500/5 border border-green-500/20 text-green-700 dark:text-green-400'
          )}
        >
          {isDemo ? (
            <>
              <WifiOff className="h-3.5 w-3.5" />
              <span className="font-medium">Using mock data</span>
            </>
          ) : (
            <>
              <Wifi className="h-3.5 w-3.5" />
              <span className="font-medium">Connected to backend</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
